import { trackPromise } from 'react-promise-tracker';
import { Box, Tab, Tabs, useTheme } from '@mui/material';
import PageHeader from '../../../components/PageHeader';
import { infectionControlIcon } from '../../../icons';
import { addError } from '../../../redux/alerts';
import constants from '../../../constants';
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../redux/store';
import { getAutoclaves, getAutoclaveTypes, getAutoclaveFrequencies, getBioboxes, getSporetests, getSporetestLogs } from '../../../services/biologicalServices';
import { Autoclave, AutoclaveType, BioboxFilters, Frequency, OpenAutoclaveModal, OpenAssignBoxModal, SporetestingResult, OpenSporetestModal, SporetestLog } from './types';
import { Biobox, LoadingState } from '../../../types';
import ComplianceWrapper from '../../../components/ComplianceWrapper';
import Autoclaves from './components/Autoclaves';
import Bioboxes from './components/Biobioxes';
import './Biological.css';
import { getBioboxesByStatus, getFormattedBioboxes } from './utils/helpers';
import AssignBoxModal from './components/AssignBoxModal';
import LoadingSection from '../../../components/LoadingSection';
import useCustomerNumber from '../../../hooks/useCustomerNumber';
import AddEditAutoclaveModal from './components/AddEditAutoclaveModal';
import { AutoclaveTypeId } from './enums';
import AddEditSporetestModal from './components/AddEditSporetestModal';
import useCustomer from '../../../hooks/useCustomer';
import useCustomerLoading from '../../../hooks/useCustomerLoading';

const Biological = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const loadDataRef = useRef<string | null>(null)

  const customer = useCustomer()
  const customerNumber = useCustomerNumber()
  const customerMetadata = customer.customerMetadata
  const customerLoading = useCustomerLoading()

  const [autoclaves, setAutoclaves] = useState<Autoclave[]>([]);
  const [autoclaveTypes, setAutoclaveTypes] = useState<AutoclaveType[]>([]);
  const [autoclaveFrequencies, setAutoclaveFrequencies] = useState<Frequency[]>([]);
  const [bioboxes, setBioboxes] = useState<Biobox[]>([]);
  const [inventoryCount, setInventoryCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [tabIndex, setTabIndex] = useState<number>(0);
  const [sporetests, setSporetests] = useState<SporetestingResult[]>([]);
  const [ sporetestLogs, setSporetestLogs ] = useState<SporetestLog[]>([])
  const [openAssignBioboxModal, setOpenAssignBioboxModal] = useState<OpenAssignBoxModal>({ open: false, autoclave: null, biobox: null })
  const [openAutoclaveModal, setOpenAutoclaveModal] = useState<OpenAutoclaveModal>({ open: false, autoclave: null })
  const [openSporetestModal, setOpenSporetestModal] = useState<OpenSporetestModal>({ open: false, autoclaveId: null, testType: '', sporetest: null })

  const pageTabs = [`Autoclaves (${autoclaves.length})`, `Inventory (${inventoryCount})`];

  const handleAddAutoclave = () => {
    setOpenAutoclaveModal({ open: true, autoclave: null })
    setOpenAssignBioboxModal({ open: false, autoclave: null, biobox: null });
    if (tabIndex !== 0) setTabIndex(0);
  }

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const loadAllData = async () => {
    if (!customerNumber) return;

    const currentCustomerNumber = customerNumber;
    loadDataRef.current = currentCustomerNumber;

    try {
      const results = await Promise.allSettled([
        getAutoclaves(currentCustomerNumber),
        getAutoclaveTypes(currentCustomerNumber),
        getAutoclaveFrequencies(currentCustomerNumber),
        getBioboxes(currentCustomerNumber),
        getSporetests(currentCustomerNumber),
        getSporetestLogs(currentCustomerNumber, null)
      ]);

      if (loadDataRef.current !== currentCustomerNumber || customerNumber !== currentCustomerNumber) {
        return;
      }

      const setters = [
        setAutoclaves,
        setAutoclaveTypes,
        setAutoclaveFrequencies,
        setBioboxes,
        setSporetests,
        setSporetestLogs
      ]

      const errors = [
        'Failed to load autoclaves',
        'Failed to load autoclave types',
        'Failed to load autoclave frequencies',
        'Failed to load bioboxes',
        'Failed to load sporetests',
        'Failed to load sporetest logs'
      ]

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          setters[index](result.value)
        } else {
          console.error(result.reason)
          dispatch(addError(errors[index]))
        }
      })

      setLoading(false);
    } catch (error: any) {
      // Check if customerNumber changed while we were loading
      if (loadDataRef.current !== currentCustomerNumber || customerNumber !== currentCustomerNumber) {
        return; // Ignore errors if customerNumber changed
      }

      setLoading(false);
      const message = error?.response?.data?.message || error?.message || 'Failed to load report';
      console.error(error);

      dispatch(addError(message));
    }
  };

  const biologicalItemCompliance = useMemo(() => {
    if (!autoclaves) return [];

    const itemCompliance = autoclaves.map(autoclave => {
      const { autoclave_compliance } = autoclave
      const { compliance_status } = autoclave_compliance.compliance

      return { compliance: { status: compliance_status } }
    })

    return itemCompliance
  }, [autoclaves]);

  const biologicalCompliance = useMemo(() => {
    if (!customerMetadata) return null;

    return {
      message_long: customerMetadata.bio_message_long,
      compliance_status: customerMetadata.bio_compliance_status
    };
  }, [customerMetadata]);

  const autoclaveTypeById = useMemo(() => {
    const typeById = new Map<AutoclaveTypeId, AutoclaveType>(
      autoclaveTypes.map(type => ( [Number(type.autoclavetype_id), type] ))
    )

    return typeById
  }, [autoclaveTypes])

  const autoclaveFrequencyById = useMemo(() => {
    const frequencyById = new Map<number, Frequency>(
      autoclaveFrequencies.map(frequency => ( [frequency.key_, frequency] ))
    )

    return frequencyById
  }, [autoclaveFrequencies])

  useEffect(() => {
    if (!customerNumber) {
      setLoading(false);
      loadDataRef.current = null;
      return;
    }

    setLoading(true);

    trackPromise(loadAllData());
  }, [customerNumber]);

  const tabContents = useMemo(
    () => [
      <Autoclaves
        key="0"
        autoclaves={autoclaves}
        autoclaveTypeById={autoclaveTypeById}
        autoclaveFrequencyById={autoclaveFrequencyById}
        bioboxes={bioboxes}
        sporetests={sporetests}
        sporetestLogs={sporetestLogs}
        reload={loadAllData}
        setTabIndex={setTabIndex}
        setAutoclaves={setAutoclaves}
        setSporetests={setSporetests}
        setOpenAssignBioboxModal={setOpenAssignBioboxModal}
        setOpenAutoclaveModal={setOpenAutoclaveModal}
        setOpenSporetestModal={setOpenSporetestModal}
      />,
      <Bioboxes
        key="1"
        bioboxes={bioboxes}
        setBioboxes={setBioboxes}
        setTabIndex={setTabIndex}
        setInventoryCount={setInventoryCount}
        setOpenAssignBioboxModal={setOpenAssignBioboxModal}
        customerNumber={customerNumber}
      />
    ],
    [autoclaves, bioboxes]
  );

  useEffect(() => {
    if (tabIndex === 0) {
      const formattedBoxes = getFormattedBioboxes(bioboxes);
      const boxesByStatus = getBioboxesByStatus(formattedBoxes);
      const inventory = boxesByStatus.get(BioboxFilters.Active) ?? [];

      setInventoryCount(inventory.length);
    }
  }, [bioboxes, tabIndex]);

  const bioLoadingState = {
    loading: loading || customerLoading === LoadingState.Loading,
    error: !loading && customerLoading === LoadingState.Error,
    done: !loading && customerLoading === LoadingState.Done,
  }

  return (
    <Box>
      <PageHeader headerName={'Biological'} size={'26px'} src={infectionControlIcon} />
      {
        bioLoadingState.loading && <LoadingSection />
      }
      {
        bioLoadingState.error && (
          <Box sx={{ p: 2 }}>
            Unable to load biological data. Please try refreshing the page.
          </Box>
        )
      }
      {
        bioLoadingState.done && autoclaves.length === 0 && bioboxes.length === 0 && (
          <iframe className="iframe-sales-page" data-testid="sales-iframe" src={constants.BIOLOGICAL_SALES_PAGE_URL} />
        )
      }
      {
        bioLoadingState.done && (Boolean(autoclaves.length) || Boolean(bioboxes.length)) && (
          <>
            <ComplianceWrapper
              type="biological"
              label="Biological"
              image="chart-biological"
              values={biologicalItemCompliance}
              compliance={biologicalCompliance}
            />
            <Box sx={{ color: theme.palette.color50555b.main }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-end">
                <Tabs value={tabIndex} onChange={handleTabChange}>
                  {pageTabs.length && pageTabs.map((tab, index) => <Tab key={index} label={tab} />)}
                </Tabs>
              </Box>
              {tabContents[tabIndex]}
            </Box>
            {
              openAssignBioboxModal.open && (
                <AssignBoxModal
                  autoclaves={autoclaves}
                  autoclaveTypeById={autoclaveTypeById}
                  sporetests={sporetests}
                  customerNumber={customerNumber}
                  openModal={openAssignBioboxModal}
                  reload={loadAllData}
                  setOpenModal={setOpenAssignBioboxModal}
                  handleAddAutoclave={handleAddAutoclave}
                />
              )
            }
            {
              openAutoclaveModal.open && (
                <AddEditAutoclaveModal
                  autoclaves={autoclaves}
                  autoclaveTypeById={autoclaveTypeById}
                  bioboxes={bioboxes}
                  sporetests={sporetests}
                  openModal={openAutoclaveModal}
                  setOpenModal={setOpenAutoclaveModal}
                  reload={loadAllData}
                />
              )
            }
            {
              openSporetestModal.open && (
                <AddEditSporetestModal
                  openModal={openSporetestModal}
                  setOpenModal={setOpenSporetestModal}
                  bioboxes={bioboxes}
                  sporetests={sporetests}
                  reload={loadAllData}
                />
              )
            }
          </> 
        )
      }
    </Box>
  )
}

export default Biological
